import React, { useCallback, useEffect, useState } from 'react'
import {
  X,
  RefreshCw,
  Ban,
  AlertTriangle,
  History,
  AlertCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { shouldCloseDialog, useEscapeClose } from '@/hooks/useEscapeClose'
import {
  listFreeConversions,
  voidFreeConversion,
  type FreeConversionItem,
} from '@/services/warehouse/freeConversionsApi'

interface FreeConversionHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  onConversionChanged: () => void
  initialConversions?: FreeConversionItem[]
}

function formatDateTimeVi(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function FreeConversionHistoryModal({
  isOpen,
  onClose,
  onConversionChanged,
  initialConversions,
}: FreeConversionHistoryModalProps): React.ReactElement | null {
  const [items, setItems] = useState<FreeConversionItem[]>(initialConversions ?? [])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Void confirmation state
  const [voidingId, setVoidingId] = useState<string | null>(null)
  const [voidReason, setVoidReason] = useState('')
  const [voidLoading, setVoidLoading] = useState(false)
  const [voidError, setVoidError] = useState<string | null>(null)

  const closeVoidDialog = useCallback(() => {
    if (!shouldCloseDialog(Boolean(voidReason.trim()))) return
    setVoidingId(null)
  }, [voidReason])
  const closeHistory = useCallback(() => {
    if (voidingId) {
      closeVoidDialog()
      return
    }
    onClose()
  }, [closeVoidDialog, onClose, voidingId])
  useEscapeClose({ enabled: isOpen, hasUnsavedChanges: Boolean(voidingId && voidReason.trim()), onClose: closeHistory })

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listFreeConversions()
      setItems(data)
    } catch (err: any) {
      setError(err?.message || 'Không thể tải lịch sử chuyển đổi')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (initialConversions !== undefined) {
      setItems(initialConversions)
      return
    }
    if (isOpen) {
      void loadData()
    }
  }, [isOpen, loadData, initialConversions])

  async function handleConfirmVoid() {
    if (!voidingId) return
    if (!voidReason.trim()) {
      setVoidError('Vui lòng nhập lý do hủy chuyển (bắt buộc)')
      return
    }

    setVoidLoading(true)
    setVoidError(null)

    try {
      await voidFreeConversion(voidingId, voidReason.trim())
      setVoidingId(null)
      setVoidReason('')
      await loadData()
      onConversionChanged()
    } catch (err: any) {
      const code = err.code || ''
      const msg = err.message || 'Lỗi khi hủy chuyển đổi'
      if (code === 'INSUFFICIENT_STOCK') {
        setVoidError(
          'Lỗi tồn kho (INSUFFICIENT_STOCK): Không đủ số lượng tồn vật tư trong kho để hoàn tác trừ kho. Có thể vật tư đã được xuất sử dụng.',
        )
      } else if (code === 'ALREADY_VOID') {
        setVoidError('Phiếu chuyển đổi này đã được hủy trước đó')
      } else {
        setVoidError(`${msg} (${code || 'ERROR'})`)
      }
    } finally {
      setVoidLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 backdrop-blur-xs font-sans"
        role="dialog"
        aria-modal="true"
        aria-labelledby="conversion-history-title"
      >
        <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-3.5">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-slate-700" />
              <div>
                <h2 id="conversion-history-title" className="text-base font-bold text-slate-900">
                  Lịch sử chuyển hàng tự do sang vật tư
                </h2>
                <p className="text-xs text-slate-500">
                  Danh sách các lần chuyển sổ cái kho tự do sang vật tư chuẩn
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void loadData()}
                disabled={loading}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <button
                type="button"
                onClick={closeHistory}
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                aria-label="Đóng"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Error banner */}
          {error ? (
            <div className="flex items-center gap-2 border-b border-rose-200 bg-rose-50 px-5 py-2.5 text-xs text-rose-800">
              <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" />
              <span>{error}</span>
            </div>
          ) : null}

          {/* Table content */}
          <div className="flex-1 overflow-y-auto p-5">
            <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)]">
              <table className="w-full border-collapse text-left text-xs">
                <thead className="sticky top-0 z-10 border-b border-[var(--color-border)] bg-slate-50 font-semibold uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="w-12 px-3 py-2.5 text-center whitespace-nowrap">STT</th>
                    <th className="w-28 min-w-[130px] px-3 py-2.5 whitespace-nowrap">Mã phiếu</th>
                    <th className="w-36 min-w-[140px] px-3 py-2.5 whitespace-nowrap">Thời gian</th>
                    <th className="w-32 min-w-[130px] px-3 py-2.5 text-right whitespace-nowrap">Xuất hàng tự do</th>
                    <th className="w-24 min-w-[80px] px-3 py-2.5 text-center whitespace-nowrap">Hệ số</th>
                    <th className="w-32 min-w-[130px] px-3 py-2.5 text-right whitespace-nowrap">Nhập vật tư</th>
                    <th className="min-w-[180px] px-3 py-2.5">Lý do</th>
                    <th className="w-28 min-w-[100px] px-3 py-2.5 text-center whitespace-nowrap">Trạng thái</th>
                    <th className="w-28 min-w-[110px] px-3 py-2.5 text-center whitespace-nowrap">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && items.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-3 py-10 text-center text-slate-400">
                        <div className="inline-flex items-center gap-2">
                          <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />
                          Đang tải lịch sử chuyển đổi...
                        </div>
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-3 py-10 text-center text-slate-400">
                        Chưa có phiếu chuyển hàng tự do sang vật tư nào
                      </td>
                    </tr>
                  ) : (
                    items.map((row, idx) => {
                      const isVoid = row.status === 'VOID'
                      return (
                        <tr key={row.id} className="hover:bg-slate-50/80">
                          {/* STT */}
                          <td className="px-3 py-2.5 text-center text-slate-400 tabular-nums whitespace-nowrap">
                            {idx + 1}
                          </td>

                          {/* Mã phiếu */}
                          <td className="px-3 py-2.5 font-mono font-bold text-slate-900 whitespace-nowrap">
                            {row.code}
                          </td>

                          {/* Thời gian */}
                          <td className="px-3 py-2.5 text-slate-600 tabular-nums whitespace-nowrap">
                            {formatDateTimeVi(row.created_at)}
                          </td>

                          {/* Số lượng xuất hàng tự do */}
                          <td className="px-3 py-2.5 text-right font-medium text-amber-800 tabular-nums whitespace-nowrap">
                            {`-${row.qty_free.toLocaleString('vi-VN')} ${row.free_uom}`}
                          </td>

                          {/* Hệ số */}
                          <td className="px-3 py-2.5 text-center font-mono text-slate-600 tabular-nums whitespace-nowrap">
                            {row.factor === 1 ? '1 : 1' : `× ${row.factor}`}
                          </td>

                          {/* Số lượng nhập vật tư */}
                          <td className="px-3 py-2.5 text-right font-bold text-emerald-700 tabular-nums whitespace-nowrap">
                            {`+${row.qty_base.toLocaleString('vi-VN')} ${row.base_uom}`}
                          </td>

                          {/* Lý do */}
                          <td className="px-3 py-2.5 text-slate-700 break-words min-w-[180px]">
                            <div>{row.reason}</div>
                            {isVoid && row.void_reason ? (
                              <div className="mt-0.5 text-[11px] text-rose-700 italic break-words">
                                Lý do hủy: {row.void_reason} ({formatDateTimeVi(row.voided_at || '')})
                              </div>
                            ) : null}
                          </td>

                          {/* Trạng thái */}
                          <td className="px-3 py-2.5 text-center whitespace-nowrap">
                            <span
                              className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-semibold uppercase ${
                                !isVoid
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : 'bg-rose-50 text-rose-700 border border-rose-200'
                              }`}
                            >
                              {!isVoid ? 'Hoạt động' : 'Đã hủy'}
                            </span>
                          </td>

                          {/* Thao tác */}
                          <td className="px-3 py-2.5 text-center whitespace-nowrap">
                            {!isVoid ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setVoidingId(row.id)
                                  setVoidReason('')
                                  setVoidError(null)
                                }}
                                className="inline-flex items-center gap-1 rounded border border-rose-200 bg-white px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-50 hover:border-rose-300 whitespace-nowrap"
                                title="Hủy phiếu chuyển đổi này"
                              >
                                <Ban className="h-3 w-3 shrink-0" />
                                Hủy chuyển
                              </button>
                            ) : (
                              <span className="text-slate-400 text-[11px] whitespace-nowrap">—</span>
                            )}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end border-t border-[var(--color-border)] px-5 py-3">
            <Button variant="secondary" onClick={closeHistory}>
              Đóng
            </Button>
          </div>
        </div>
      </div>

      {/* Void Dialog */}
      {voidingId ? (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 p-4 font-sans"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-5 shadow-2xl">
            <div className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-sm font-bold text-slate-900">
                Xác nhận hủy phiếu chuyển đổi
              </h3>
            </div>
            <p className="mt-2 text-xs text-slate-600">
              Thao tác này sẽ hoàn trả số lượng về kho hàng tự do và trừ ngược khỏi tồn kho vật tư.
            </p>

            {voidError ? (
              <div className="mt-3 rounded border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-800">
                {voidError}
              </div>
            ) : null}

            <div className="mt-3">
              <label className="mb-1 block text-xs font-bold text-slate-700">
                Lý do hủy <span className="text-rose-600">*</span>
              </label>
              <textarea
                rows={3}
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Nhập lý do hủy chuyển sang vật tư (bắt buộc)..."
                className="w-full rounded border border-slate-300 p-2 text-xs outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={closeVoidDialog}
                disabled={voidLoading}
              >
                Đóng
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirmVoid}
                disabled={voidLoading || !voidReason.trim()}
                className="bg-rose-600 hover:bg-rose-700"
              >
                {voidLoading ? (
                  <>
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Đang hủy...
                  </>
                ) : (
                  'Xác nhận hủy'
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
